import { HireARoleLayout, HireCard } from '@/components/HireARole'
import { Loading } from '@/components/shared/basic'
import { pageTitle } from '@/utils/static-helpers/pageTitle'
import { notify } from '@qureos/lib'
import { AxiosError } from 'axios'
import Cookies from 'js-cookie'
import { uniqBy } from 'lodash'
import { NextPage } from 'next'
import Head from 'next/head'
import React, { useEffect } from 'react'
import {
  AxiosErrorResponse,
  formatAxiosError
} from 'src/services/format-axios-error'
import { getInstantJobDetails, getInstantJobs } from 'src/services/instantJobs'

interface HirePageProps {
  job: any
  detailsLoading?: boolean
  metaTitle?: string
  metaDescription?: string
  instantJobs: any
  saudiJobs: any
  uaeJobs: any
  canonicalUrl?: string
}

const Hire: NextPage<HirePageProps> = ({
  job,
  detailsLoading: loading,
  metaTitle,
  metaDescription,
  instantJobs,
  saudiJobs,
  uaeJobs,
  canonicalUrl
}) => {
  const [jobDetails, setJobDetails] = React.useState<any>({})
  const [detailsLoading, setDetailsLoading] = React.useState(loading)

  useEffect(() => {
    if (job) {
      setJobDetails(job)
      let themes = job?.theme

      if (!Array.isArray(themes)) {
        themes = themes ? [themes] : []
      } else {
        themes = [...themes]
      }

      themes.forEach(theme => {
        theme.value = theme._id
      })

      Cookies.set('departmentFilter', job?.theme ? themes : [])
      setDetailsLoading(false)
    } else {
      notify({
        message: job?.data?.message ?? 'Unable to get job details',
        type: 'error'
      })
    }
  }, [job])
  return (
    <>
      <Head>
        <title>{pageTitle(metaTitle ?? jobDetails.title)}</title>
        <meta name="description" content={metaDescription} />
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      </Head>
      <HireARoleLayout>
        {detailsLoading ? (
          <div className="pt-52 w-full">
            <Loading height="h-24" />
          </div>
        ) : (
          <HireCard
            jobData={jobDetails}
            instantJobs={instantJobs}
            saudiJobs={saudiJobs}
            uaeJobs={uaeJobs}
          />
        )}
      </HireARoleLayout>
    </>
  )
}

export async function getServerSideProps(context) {
  const id = context.query.id
  let job = null
  let instantJobs = null

  const handleError = (error: AxiosError<AxiosErrorResponse>) => {
    const errorResponse = formatAxiosError(error)
    if (!errorResponse || errorResponse.statusCode !== 401) {
      notify({
        message: 'Something went wrong, you may need to refresh your page',
        type: 'error'
      })
      return
    }
    notify({
      message: 'You are not authorized to view this job',
      type: 'error'
    })
  }
  const [uaeJobs, saudiJobs] = await Promise.all([
    getInstantJobs('United Arab Emirates', 10),
    getInstantJobs('Saudi Arabia', 10)
  ])

  try {
    if (id) {
      job = await getInstantJobDetails(id)

      // if (job) country = job?.country

      instantJobs = await getInstantJobs(job?.country, 10)
    } else {
      throw new Error('id is not present')
    }
  } catch (error) {
    handleError(error)
  }

  return {
    props: {
      job: job || null,
      jobId: id,
      detailsLoading: true,
      metaTitle: `Hire the best ${job?.title} in ${job?.location} In a Single Click!`,
      metaDescription: `Qureos provides highly qualified candidates for  ${job?.title} positions in ${job?.location}. Start your hiring process now and find the perfect fit for your team.`,
      instantJobs: uniqBy(instantJobs, 'title'),
      saudiJobs: uniqBy(saudiJobs, 'title'),
      uaeJobs: uniqBy(uaeJobs, 'title'),
      canonicalUrl: `${process.env.APP_URL}/hire/${job?.slug}`
    }
  }
}

export default Hire
